"use client";

import Explore from "@mui/icons-material/Explore";
import Home from "@mui/icons-material/Home";
import Person from "@mui/icons-material/Person";
import RestaurantMenu from "@mui/icons-material/RestaurantMenu";
import Waves from "@mui/icons-material/Waves";
import Box from "@mui/material/Box";
import Call from "@mui/icons-material/Call";
import LocationOn from "@mui/icons-material/LocationOn";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import NextLink from "next/link";
import React from "react";

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  title: string;
  links?: FooterLink[];
  lines?: { icon: string; text: string }[];
}

export interface MobileNavItem {
  icon: string;
  href: string;
  active?: boolean;
  dot?: boolean;
}

export interface FooterLuxeProps {
  columns?: FooterColumn[];
  copyright?: string;
  brandLeft?: string;
  zaloIconUrl?: string;
  mobileNav?: MobileNavItem[];
}

const iconByName: Record<string, React.ElementType> = {
  home: Home,
  explore: Explore,
  restaurant_menu: RestaurantMenu,
  person: Person,
  location_on: LocationOn,
  call: Call,
};

const FooterLuxe: React.FC<FooterLuxeProps> = ({
  columns = [],
  copyright = "",
  brandLeft = "LUXE",
  zaloIconUrl = "",
  mobileNav = [],
}) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <>
      <Box
        component="footer"
        sx={{
          position: "relative",
          width: "100%",
          mt: 12,
          bgcolor: "#fff",
          borderTop: "1px solid #f1f5f9",
        }}
      >
        <Stack
          alignItems="center"
          spacing={4}
          sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 3 }, maxWidth: "lg", mx: "auto" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Waves sx={{ color: primary, fontSize: 40 }} />
            <Typography
              sx={{
                fontWeight: 900,
                fontStyle: "italic",
                fontSize: { xs: "1.75rem", md: "1.875rem" },
                letterSpacing: "-0.03em",
                color: "text.primary",
              }}
            >
              {brandLeft}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: 4, md: 6 },
              width: "100%",
              textAlign: { xs: "left", md: "center" },
              mt: 2,
            }}
          >
            {columns.map((col) => (
              <Stack key={col.title} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
                <Typography
                  sx={{
                    color: primary,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                  }}
                >
                  {col.title}
                </Typography>
                <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {col.links?.map((l) => (
                    <Box component="li" key={l.label}>
                      <Link
                        href={l.href}
                        underline="hover"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          "&:hover": { color: primary },
                        }}
                      >
                        {l.label}
                      </Link>
                    </Box>
                  ))}
                  {col.lines?.map((line) => {
                    const Icon = iconByName[line.icon] || LocationOn;
                    return (
                      <Stack
                        component="li"
                        key={line.text}
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        justifyContent={{ xs: "flex-start", md: "center" }}
                        sx={{ color: "text.secondary", fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        <Icon sx={{ fontSize: 14 }} />
                        {line.text}
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            ))}
          </Box>

          <Box sx={{ width: "100%", height: 1, bgcolor: "#f1f5f9", mt: 2 }} />

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "10px",
              fontWeight: 800,
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              textAlign: "center",
            }}
          >
            {copyright}
          </Typography>
        </Stack>
      </Box>

      {/* Mobile bottom navigation */}
      <Box
        component="nav"
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 50,
          alignItems: "center",
          gap: 5,
          bgcolor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(16px)",
          borderRadius: 999,
          px: 4,
          py: 2,
          border: "1px solid #e2e8f0",
          boxShadow: "0 20px 40px -12px rgba(0,0,0,0.15)",
        }}
        aria-label="Mobile navigation"
      >
        {mobileNav.map((item) => {
          const Icon = iconByName[item.icon] || Home;
          const isPrimary = item.active ? primary : theme.palette.text.secondary;
          return (
            <Box key={item.icon + item.href} sx={{ position: "relative" }}>
              {item.href.startsWith("/") && !item.href.startsWith("//") ? (
                <NextLink href={item.href} passHref legacyBehavior>
                  <IconButton
                    component="a"
                    sx={{
                      color: isPrimary,
                      transform: item.active ? "scale(1.25)" : undefined,
                      transition: "color 0.3s, transform 0.3s",
                    }}
                  >
                    <Icon />
                  </IconButton>
                </NextLink>
              ) : (
                <IconButton
                  component="a"
                  href={item.href}
                  sx={{
                    color: isPrimary,
                    transform: item.active ? "scale(1.25)" : undefined,
                    transition: "color 0.3s, transform 0.3s",
                  }}
                >
                  <Icon />
                </IconButton>
              )}
              {item.dot ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#FF4D6D",
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>

      {/* Zalo floating */}
      {zaloIconUrl ? (
        <Box
          sx={{
            position: "fixed",
            right: 24,
            bottom: { xs: 100, md: 24 },
            zIndex: 40,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <IconButton
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#fff",
              border: `1px solid ${primary}66`,
              color: primary,
              boxShadow: "0 12px 32px -8px rgba(0,0,0,0.15)",
              "&:hover": { transform: "scale(1.1)" },
            }}
            aria-label="Zalo"
          >
            <Box component="img" src={zaloIconUrl} alt="Zalo" sx={{ width: 28, height: 28 }} />
          </IconButton>
        </Box>
      ) : null}

      {/* Spacer for fixed mobile nav */}
      <Box sx={{ display: { xs: "block", md: "none" }, height: 72 }} />
    </>
  );
};

export default FooterLuxe;
